import * as Yup from "yup";
import { REQUIRED_FIELD } from "constants/validator-messages";
import { AccountLeadStore } from "store/UserAccounts/UserAccount/AccountLeads/AccountLead.store";
import { FC, useCallback, useMemo } from "react";
import { observer } from "mobx-react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { ActionPromptContainer, FormControlV2, Message } from "components";
import { Box, Button, Divider } from "@chakra-ui/react";
import { AlertStatusEnum } from "enums/alert-status.enum";
import { FormControlsTypeEnum } from "enums/form-controls-type.enum";
import { TLeadAltoAddress } from "types/alto.type";
import { getAltoAddressFields } from "utils/alto.utils";

type TFieldValues = TLeadAltoAddress;

const validationSchema = Yup.object().shape({
  nameNumber: Yup.string().required(REQUIRED_FIELD),
  street: Yup.string().required(REQUIRED_FIELD),
  locality: Yup.string(),
  town: Yup.string(),
  county: Yup.string(),
  postcode: Yup.string().required(REQUIRED_FIELD),
});

interface IProps {
  leadStore: AccountLeadStore;
  handleSyncLead: (address: TLeadAltoAddress) => Promise<void>;
  closePrompt: () => void;
}

export const LeadAltoSyncFormPrompt: FC<IProps> = observer(
  ({ leadStore, handleSyncLead, closePrompt }) => {
    const { address, postcode } = leadStore.lead;

    const initialValues: TFieldValues = useMemo(() => {
      const segmentedAddress = getAltoAddressFields(address, postcode);
      return {
        nameNumber: segmentedAddress.nameNumber || "",
        street: segmentedAddress.street || "",
        locality: segmentedAddress.locality || "",
        subDwelling: segmentedAddress.subDwelling || "",
        town: segmentedAddress.town || "",
        county: segmentedAddress.county || "",
        postcode: segmentedAddress.postcode || "",
      };
    }, [address, postcode]);

    const {
      handleSubmit,
      control,
      formState: { isSubmitting },
    } = useForm<TFieldValues>({
      defaultValues: initialValues,
      mode: "onSubmit",
      resolver: yupResolver(validationSchema),
    });

    const onSubmit = useCallback(
      async (values: TLeadAltoAddress) => {
        await handleSyncLead(values);
        closePrompt();
      },
      [handleSyncLead, closePrompt]
    );

    return (
      <ActionPromptContainer
        header={"Sync lead to Alto"}
        body={
          <Box pt={5}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Message status={AlertStatusEnum.WARNING} mb={5}>
                <Box>
                  {`Please confirm the address below is correct before syncing to Alto.`}
                </Box>
              </Message>
              <Box mb={5}>
                <FormControlV2<TFieldValues>
                  name={"nameNumber"}
                  control={control}
                  label={"House number/name"}
                  type={FormControlsTypeEnum.TEXT}
                />
              </Box>
              <Box mb={5}>
                <FormControlV2<TFieldValues>
                  name={"street"}
                  control={control}
                  label={"Street"}
                  type={FormControlsTypeEnum.TEXT}
                />
              </Box>
              <Box mb={5}>
                <FormControlV2<TFieldValues>
                  name={"locality"}
                  control={control}
                  label={"Locality"}
                  isOptional
                  type={FormControlsTypeEnum.TEXT}
                />
              </Box>
              <Box mb={5}>
                <FormControlV2<TFieldValues>
                  name={"town"}
                  control={control}
                  label={"Town"}
                  isOptional
                  type={FormControlsTypeEnum.TEXT}
                />
              </Box>
              <Box mb={5}>
                <FormControlV2<TFieldValues>
                  name={"county"}
                  control={control}
                  label={"County"}
                  isOptional
                  type={FormControlsTypeEnum.TEXT}
                />
              </Box>
              <Box mb={5}>
                <FormControlV2<TFieldValues>
                  name={"postcode"}
                  control={control}
                  label={"Postcode"}
                  type={FormControlsTypeEnum.TEXT}
                />
              </Box>

              <Divider mb={4} />
              <Box
                width={"100%"}
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"flex-end"}
              >
                <Button
                  type={"submit"}
                  colorScheme={"blue"}
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                >
                  Sync to Alto
                </Button>
              </Box>
            </form>
          </Box>
        }
      />
    );
  }
);
